import { Box } from '@mui/material';
import { useEffect, useState } from 'react';

import { getCachedImageUrl, loadCachedImage } from './utils/imageCache';

interface ImageCardImageProps {
  alt: string;
  src: string;
  onImageLoad?: (image: HTMLImageElement) => void;
  objectFit?: 'fill' | 'contain' | 'cover';
}

const imageLoadingIconSrc =
  'https://storage.googleapis.com/dotstorming-img/dotstorming-logo-compact.gif';

export function ImageCardImage({ alt, onImageLoad, objectFit = 'fill', src }: ImageCardImageProps) {
  const [imageSrc, setImageSrc] = useState(() => getCachedImageUrl(src));
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoading = imageSrc === null || loadedSrc !== imageSrc;

  useEffect(() => {
    let isCurrent = true;
    const cachedImageUrl = getCachedImageUrl(src);

    setImageSrc(cachedImageUrl);
    setLoadedSrc(null);

    if (cachedImageUrl) {
      return () => {
        isCurrent = false;
      };
    }

    loadCachedImage(src)
      .then((cachedImageUrl) => {
        if (isCurrent) {
          setImageSrc(cachedImageUrl);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setImageSrc(src);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [src]);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flex: '1 1 auto',
        height: '100%',
        justifyContent: 'center',
        minHeight: 80,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      {isLoading && (
        <Box
          component="img"
          alt=""
          aria-hidden="true"
          src={imageLoadingIconSrc}
          sx={{
            height: 39,
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 39,
            zIndex: 1,
          }}
        />
      )}
      {imageSrc && (
        <Box
          component="img"
          alt={alt}
          src={imageSrc}
          draggable={false}
          onLoad={(event) => {
            setLoadedSrc(imageSrc);
            onImageLoad?.(event.currentTarget);
          }}
          onError={() => {
            setLoadedSrc(imageSrc);
          }}
          sx={{
            display: 'block',
            height: '100%',
            inset: 0,
            minHeight: 80,
            objectFit,
            objectPosition: 'center',
            opacity: isLoading ? 0 : 1,
            position: 'absolute',
            width: '100%',
          }}
        />
      )}
    </Box>
  );
}
